// Pure shaping and formatting for the "continue where you left off" surfaces.
// Kept free of React and of the network so the grouping rule — the thing that
// decides whether a student is shown a picker at all — is unit-testable.

import type { OpenAttemptKind, OpenAttemptRow } from './continue-api';

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;

export const KIND_LABEL: Record<OpenAttemptKind, string> = {
  scheduled: 'Mock',
  practice: 'Practice',
  assignment: 'Assignment',
};

/** Where resuming this attempt goes. A scheduled sitting is keyed on its
 *  session; everything else is keyed on the attempt itself. */
export function resumePath(row: OpenAttemptRow): string {
  if (row.kind === 'scheduled' && row.session_id !== null) {
    return `/student/exam/${row.session_id}`;
  }
  return `/student/practice/${row.attempt_id}`;
}

/**
 * Human time remaining. Deliberately coarse — a ticking clock on a list is
 * noise, and a rounded figure is what a student actually decides on ("do I
 * have time for this now?").
 */
export function formatTimeLeft(ms: number): string {
  if (ms <= 0) return 'No time left';
  if (ms < MINUTE_MS) return 'Under a minute left';
  if (ms < HOUR_MS) return `${String(Math.round(ms / MINUTE_MS))} min left`;
  const hours = Math.floor(ms / HOUR_MS);
  const minutes = Math.round((ms % HOUR_MS) / MINUTE_MS);
  // 1 h 60 min is nobody's idea of an hour.
  if (minutes === 60) return `${String(hours + 1)} h left`;
  return minutes === 0
    ? `${String(hours)} h left`
    : `${String(hours)} h ${String(minutes)} min left`;
}

/** "Yesterday", "3 days ago" — the last time they touched it. */
export function formatLastActive(iso: string, nowMs: number = Date.now()): string {
  const then = Date.parse(iso);
  if (!Number.isFinite(then)) return 'Recently';
  const diff = Math.max(0, nowMs - then);
  if (diff < 2 * MINUTE_MS) return 'Just now';
  if (diff < HOUR_MS) return `${String(Math.round(diff / MINUTE_MS))} minutes ago`;
  if (diff < 24 * HOUR_MS) {
    const hours = Math.round(diff / HOUR_MS);
    return hours === 1 ? 'An hour ago' : `${String(hours)} hours ago`;
  }
  const days = Math.round(diff / (24 * HOUR_MS));
  return days === 1 ? 'Yesterday' : `${String(days)} days ago`;
}

/**
 * Test statuses an attempt already under way can still be finished in.
 *
 * Archiving (Phase 197) stops NEW attempts; it deliberately leaves one that is
 * half done finishable, and nothing about the paper's content changes. Only a
 * test pulled back to draft has nothing dependable left to render.
 */
function isFinishable(testStatus: string): boolean {
  return testStatus === 'published' || testStatus === 'archived';
}

/**
 * What the student needs told about this row, or null when there is nothing to
 * say.
 *
 * Only a SCHEDULED sitting can genuinely expire: its clock never stopped and
 * its window is booked. Practice and assignments always come back, because
 * resuming restores the time they had — that is the whole point.
 */
export function blockedReason(row: OpenAttemptRow): string | null {
  if (!isFinishable(row.test_status)) {
    return 'Your teacher has unpublished this test. Ask them to publish it again.';
  }
  if (row.kind === 'scheduled') {
    if (row.session_status === 'cancelled') return 'Your institute cancelled this session.';
    if (row.remaining_ms <= 0) {
      return 'The exam window has closed — go in to submit what you have.';
    }
  }
  return null;
}

/**
 * Whether the student can still open this attempt.
 *
 * Deliberately NOT the inverse of {@link blockedReason}: a scheduled sitting
 * whose window has closed still has to be reachable, because going in is the
 * only way to submit the answers already given. Only a test that has been
 * unpublished (nothing to render) or a cancelled session (nothing to submit
 * into) is a genuine dead end. An ARCHIVED test is neither.
 */
export function canEnter(row: OpenAttemptRow): boolean {
  if (!isFinishable(row.test_status)) return false;
  return !(row.kind === 'scheduled' && row.session_status === 'cancelled');
}

export interface OpenAttemptGroup {
  testId: string;
  testTitle: string;
  attempts: OpenAttemptRow[];
}

/**
 * Group unfinished attempts by test, newest-active first within each group and
 * across groups.
 *
 * A group holding MORE THAN ONE attempt is exactly the case where the student
 * has to be asked which one to continue — an assignment and a practice run on
 * the same paper, say, or a booked mock alongside self-study. One attempt per
 * group is the common case and must never cost an extra click.
 */
export function groupOpenAttempts(rows: readonly OpenAttemptRow[]): OpenAttemptGroup[] {
  const order: string[] = [];
  const byTest = new Map<string, OpenAttemptGroup>();
  for (const row of rows) {
    let group = byTest.get(row.test_id);
    if (group === undefined) {
      group = { testId: row.test_id, testTitle: row.test_title, attempts: [] };
      byTest.set(row.test_id, group);
      order.push(row.test_id);
    }
    group.attempts.push(row);
  }
  return order.map((id) => byTest.get(id)).filter((g): g is OpenAttemptGroup => g !== undefined);
}
